import React from "react";

interface YouTubeEmbedProps {
  url: string | null;
  memberName: string;
  fallbackImageUrl: string | null;
}

const extractYouTubeVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "youtu.be") {
      return parsed.pathname.slice(1) || null;
    }
    if (
      parsed.hostname === "www.youtube.com" ||
      parsed.hostname === "youtube.com" ||
      parsed.hostname === "m.youtube.com"
    ) {
      const v = parsed.searchParams.get("v");
      if (v) return v;
      const pathParts = parsed.pathname.split("/").filter(Boolean);
      if (pathParts[0] === "embed" && pathParts[1]) return pathParts[1];
      if (pathParts[0] === "shorts" && pathParts[1]) return pathParts[1];
    }
    return null;
  } catch {
    return null;
  }
};

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  url,
  memberName,
  fallbackImageUrl,
}) => {
  const videoId = url ? extractYouTubeVideoId(url) : null;

  if (videoId) {
    return (
      <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-video">
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}`}
          title={`${memberName} intro video`}
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 dark:border-slate-700">
      {fallbackImageUrl ? (
        <img
          alt={memberName}
          className="absolute inset-0 h-full w-full object-cover"
          src={fallbackImageUrl}
        />
      ) : (
        <div className="absolute inset-0 bg-gray-100 dark:bg-slate-800" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/65" />
      <div className="relative flex h-full w-full flex-col items-center justify-center px-6 text-center">
        <span className="flex h-14 w-20 items-center justify-center rounded-2xl bg-[#FF0000] shadow-lg">
          <svg
            aria-hidden="true"
            className="h-7 w-7"
            fill="white"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5 3.5v17l15-8.5-15-8.5z" />
          </svg>
        </span>
        <p className="mt-4 text-xs font-semibold tracking-widest uppercase text-white/80">
          YouTube Video
        </p>
        <p className="mt-1 text-lg font-semibold text-white">Coming Soon</p>
        <p className="mt-1 text-sm text-white/80">
          An intro video for {memberName} will be added here shortly.
        </p>
      </div>
    </div>
  );
};

export default YouTubeEmbed;
